import ProtectedLegalPage from "@/components/ProtectedLegalPage";
import { Check, X } from "lucide-react";

const SETS_APART = [
  { num: "1", title: "Truly Offline", body: "Techno Sort runs 100% on your machine. Documents never leave your network. The Software does not contact any external server, except for a one-time license activation (a few hundred bytes confirming your license is valid). There is no telemetry, no analytics, no usage tracking, no cloud backup, no \"helpful\" sync. If your machine is air-gapped after activation, Techno Sort still works." },
  { num: "2", title: "One-Time License", body: "Buy it once. No monthly bills. No renewal anxiety. No vendor that can hold your data hostage when their pricing changes next quarter. The Lifetime License covers you forever on one machine. Pro 1-Year exists for customers who prefer annual budgeting. The Refill Pack scales document quotas without upgrading the whole license. Three SKUs. Transparent pricing. No surprise invoices." },
  { num: "3", title: "Single EXE Install", body: "Techno Sort is a single Windows executable. No Python install. No Docker container. No Kubernetes cluster. No SQL Server. No cloud account. No vendor portal. No agent on each desktop. Download the installer, double-click, enter your license key, choose your input and destination folders, and start sorting." },
];

const COST_TABLE = [
  ["100 documents per day",   "1 hour per day",                "USD 12,500 per year"],
  ["300 documents per day",   "3 hours per day",               "USD 37,500 per year"],
  ["1,000 documents per day", "10 hours per day (1.25 FTE)",   "USD 125,000 per year"],
  ["3,000 documents per day", "30 hours per day (3.75 FTE)",   "USD 375,000 per year"],
];

const PRICE_TABLE = [
  ["Free Trial",                "USD 0",            "300 documents"],
  ["Refill Pack (during trial)","USD 10",           "+1,500 documents"],
  ["Pro 1-Year License",        "USD 199 / year",   "14,999 documents per year"],
  ["Lifetime License",          "USD 499 once",     "Unlimited, forever"],
];

const SAAS_VS = [
  ["Pricing model",                "One-time USD 499",                       "USD 50–300 per user per month"],
  ["5-year cost (1 user)",         "USD 499",                                "USD 3,000 – 18,000"],
  ["5-year cost (5 users)",        "USD 2,495 (5 machines)",                 "USD 15,000 – 90,000"],
  ["5-year cost (20 users)",       "USD 9,980 (20 machines)",                "USD 60,000 – 360,000"],
  ["Data location",                "Your hardware only",                     "Vendor cloud"],
  ["Works without internet",       "Yes",                                    "No (cloud DMS), partial (hybrid)"],
  ["Vendor can access your docs",  "No — architecturally impossible",        "Yes (cloud DMS holds the data)"],
  ["Risk if vendor goes out",      "Software keeps working",                 "Data lock-in, migration project"],
  ["Setup time",                   "5 minutes",                              "Days to months"],
  ["IT requirements",              "Windows 10 / 11 / Server",               "Cloud account, SSO, agents, training"],
  ["Per-user / per-month escalation","None",                                 "Common at renewal time"],
];

const TIER_MATRIX = [
  ["Document classification",        true, true, true],
  ["AI fallback for ambiguous files",true, true, true],
  ["OCR for scanned PDFs and images",true, true, true],
  ["Multi-source folder watch",      true, true, true],
  ["Duplicate detection (SHA-256)",  true, true, true],
  ["Versioning of repeat files",     true, true, true],
  ["Reports and Excel export",       true, true, true],
  ["Advanced Settings (worker tuning, etc.)", false, true, true],
  ["Scheduler for recurring runs",   false, true, true],
  ["Email alerts on run complete",   false, true, true],
  ["Optional cloud backup of results",false, true, true],
  ["Master Reset and Recovery tools",false, true, true],
];

const MYTHS = [
  { m: "Local AI cannot be as accurate as cloud AI.", r: "Not true for document classification. The local AI used by Techno Sort is purpose-built for the task and combines with your own user-defined rules. In practice, accuracy meets or exceeds generic cloud DMS classifiers on real customer data because Techno Sort can be trained on YOUR documents, not someone else's." },
  { m: "One-time pricing means no updates.", r: "Updates are included for both Pro 1-Year (during the active year) and Lifetime License (as long as the Software is actively maintained). One-time pricing means no monthly fee, not no maintenance." },
  { m: "Offline software cannot scale.", r: "Techno Sort is benchmarked for tens of thousands of documents per hour on commodity Windows hardware. The Auto-load balancer adjusts worker count, batch size, and memory caps based on live CPU and queue depth — so it scales up on a powerful server and scales down on a modest laptop." },
  { m: "On-prem means our IT team has to babysit it.", r: "Techno Sort is a single Windows EXE with a built-in scheduler, email alerts, Health page, automatic recovery, and a Master Reset switch. After 5 minutes of initial setup, it runs unattended. The IT team sees it on the QA & Health page if anything ever needs attention." },
  { m: "We need cloud DMS for compliance and audit.", r: "On-prem document tools meet more compliance regimes than cloud DMS, not fewer. Techno Sort generates per-run reports, immutable logs, SHA-256 audit trails, and verify-after-write checks. Your auditors will be relieved that they do not need to chase your cloud DMS vendor for evidence." },
  { m: "Single EXE means it lacks security.", r: "Single EXE means no attack surface beyond your own Windows machine. There is no admin portal to phish, no API token to leak, no cloud account to compromise, no vendor breach to inherit. License activation uses a cryptographic signature; the machine fingerprint cannot be forged. The smallest attack surface in the document classification market." },
];

export default function WhyTechnoSort() {
  return (
    <ProtectedLegalPage title="Why Techno Sort" lastUpdated="February 2026">
      <p><em>Offline. One-Time. Done. The on-prem document classifier.</em></p>

      <h2>The Hook — Your team isn't paid to drag files into folders.</h2>
      <p>
        Every business stores documents. Few store them well. Across law firms, hospitals, accounting practices, banks, government offices, manufacturing plants, and software vendors, the same scene plays out every day: an inbox of mixed PDFs, scanned images, Word files, and spreadsheets piles up, and someone has to open each one, decide what it is, and drag it to the right folder. That someone is usually a paid employee whose actual job description has nothing to do with file management.
      </p>
      <p>
        Techno Sort ends that scene. It reads every PDF, Word, Excel, and scanned image you drop into a folder, identifies what each document is, and routes it to the right destination on your existing folder structure. Fully offline. No monthly subscription. One-time purchase. No SaaS lock-in. No login screen. No cloud account. Just sort.
      </p>

      <h2>The 60 Seconds</h2>
      <p><strong>Sort 50,000 documents in the time it takes to make coffee.</strong></p>
      <p>
        Techno Sort reads every PDF, DOCX, XLSX, and scanned image in your input folder, figures out what each one is using a combination of local AI and your own classification rules, and files it where it belongs. Drop them in. Walk away. Come back to a perfectly organized archive. One-time license. Pays for itself in a week.
      </p>
      <p><strong>Your documents never touch the internet.</strong> Techno Sort runs 100% on your machine. Local AI. Local files. Local control. The only thing leaving your network is the staff time you no longer need to burn on manual filing.</p>
      <p><strong>Your files don't need a subscription.</strong> Techno Sort classifies and routes documents on YOUR hardware. No cloud. No login. No monthly bill. Buy it once. Use it forever.</p>
      <p><strong>One EXE. Zero dependencies. Done.</strong> No Python. No Docker. No Kubernetes. No cloud account. No vendor portal. Drop the installer on a Windows machine and Techno Sort starts sorting documents within minutes.</p>

      <h2>Three Things That Set Techno Sort Apart</h2>
      <p>Every claim below is enforced by the architecture, not by a marketing promise.</p>
      {SETS_APART.map((s) => (
        <div key={s.num}>
          <h3>{s.num}. {s.title}</h3>
          <p>{s.body}</p>
        </div>
      ))}

      <h2>Who Techno Sort Is For</h2>

      <h3>For IT Administrators</h3>
      <p>You have spent the last decade deploying tools that promised "just works" and delivered "just opens a ticket." Techno Sort actually deploys in 5 minutes. Single EXE, runs as a normal Windows service or desktop app, no special permissions beyond writing to the folders you point it at. No cloud account to provision. No SSO to wire up unless you want to. No data leaves the LAN. You can read the entire process flow on one page.</p>

      <h3>For Founders and Operations Heads</h3>
      <p>Your team's time is too expensive to spend on manual file sorting. Techno Sort replaces hours per day of clerical work with a one-time license payment. The math is simple: if one employee spends two hours a day sorting documents at an average loaded cost of USD 50 per hour, that is USD 25,000 per year. The Lifetime License pays for itself in one week.</p>

      <h3>For DMS Vendors and IT Resellers</h3>
      <p>Techno Sort is the on-prem document classifier you can confidently white-label, bundle, or recommend. Per-machine licensing means predictable margin. No vendor account intervenes between you and your customer. You can integrate Techno Sort into your existing DMS, ERP, or workflow product without sharing data with us. Contact us by email for reseller arrangements.</p>

      <h3>For Compliance-Conscious Industries</h3>
      <p>Law firms, accounting practices, hospitals, clinics, banks, brokerages, government offices, defence contractors, and any organization that handles regulated data find Techno Sort easy to recommend to their security team: it does not phone home, it cannot leak documents to a third party because it never touches the internet for document content, and it leaves no telemetry footprint. The license activation step transmits only a license key and a hash; no document data ever leaves the building.</p>

      <h2>Why Offline Matters</h2>
      <p>Cloud-first document management was the dominant story from 2015 to 2024. Then four things happened:</p>
      <ul>
        <li><strong>Subscription fatigue.</strong> CFOs started counting SaaS line items and asking why a tool that sorts files costs USD 200 per seat per month.</li>
        <li><strong>Data sovereignty regulations tightened.</strong> EU GDPR, UK Data Protection Act, US state-level privacy laws, and industry-specific rules (HIPAA, FERPA, GLBA, PCI-DSS) made cloud audit increasingly painful.</li>
        <li><strong>High-profile breaches at cloud DMS vendors.</strong> Each breach included thousands of client documents from unrelated companies in the leak.</li>
        <li><strong>AI moved on-device.</strong> Local AI models are now fast enough and accurate enough for document classification. The reason to send documents to the cloud (AI quality) disappeared.</li>
      </ul>
      <p>Techno Sort is built for this moment. Local AI plus rule-based classification, running on the user's own hardware, with no internet dependency for document processing, is now both technically possible and commercially preferred.</p>

      <h2>The Math — What Manual Sorting Costs You</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-sm border border-white/10">
          <thead>
            <tr className="bg-white/[0.04]">
              <th className="text-left p-3 border-b border-white/10">Quantity</th>
              <th className="text-left p-3 border-b border-white/10">Time spent</th>
              <th className="text-left p-3 border-b border-white/10">Cost (at USD 50/hour loaded)</th>
            </tr>
          </thead>
          <tbody>
            {COST_TABLE.map((row, i) => (
              <tr key={i} className="border-b border-white/5 last:border-0">
                {row.map((cell, j) => <td key={j} className="p-3">{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>What Techno Sort Costs You</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-sm border border-white/10">
          <thead>
            <tr className="bg-white/[0.04]">
              <th className="text-left p-3 border-b border-white/10">Tier</th>
              <th className="text-left p-3 border-b border-white/10">One-time cost</th>
              <th className="text-left p-3 border-b border-white/10">Document quota</th>
            </tr>
          </thead>
          <tbody>
            {PRICE_TABLE.map((row, i) => (
              <tr key={i} className="border-b border-white/5 last:border-0">
                <td className="p-3 text-white font-medium">{row[0]}</td>
                <td className="p-3 text-[#F4A22A]">{row[1]}</td>
                <td className="p-3">{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p><strong>Pay-back period: typically under one week.</strong> At even the smallest workload of 100 documents per day, manual sorting costs about USD 35 per business day. The Lifetime License at USD 499 pays for itself in 14 business days. At 300 documents per day, payback is under 5 days. At 1,000 documents per day, payback is under 2 days. After that, every saved hour is profit.</p>

      <h2>Techno Sort vs SaaS Document Management</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-sm border border-white/10">
          <thead>
            <tr className="bg-white/[0.04]">
              <th className="text-left p-3 border-b border-white/10"></th>
              <th className="text-left p-3 border-b border-white/10 text-[#F4A22A]">Techno Sort Lifetime</th>
              <th className="text-left p-3 border-b border-white/10 text-neutral-400">Typical SaaS DMS</th>
            </tr>
          </thead>
          <tbody>
            {SAAS_VS.map((row, i) => (
              <tr key={i} className="border-b border-white/5 last:border-0">
                <td className="p-3 text-neutral-400">{row[0]}</td>
                <td className="p-3 text-white">{row[1]}</td>
                <td className="p-3 text-neutral-500">{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>How It Works — Three Steps</h2>
      <h3>Step 1 — You Configure Once</h3>
      <p>Open Techno Sort. Tell it where your input folder is (e.g. C:\Inbox), and where each destination folder should be (e.g. D:\Sorted\Invoices, D:\Sorted\Contracts, D:\Sorted\HR-Documents). Add a few classification rules or train Techno Sort on sample files for each category. Setup takes about 15 minutes for a typical office. The categories you set up live forever.</p>

      <h3>Step 2 — You Drop Files In</h3>
      <p>Drop one file, ten files, ten thousand files into the input folder. Techno Sort watches the folder. It picks up new files automatically as soon as they arrive. You do not need to click anything, schedule anything, or restart anything.</p>

      <h3>Step 3 — Techno Sort Reads, Decides, Files</h3>
      <p>For each file, Techno Sort opens it, extracts text (and OCRs images and scanned PDFs), compares it against your classification rules and trained AI patterns, picks the best-matching category, and moves the file to the corresponding destination folder. Optionally it appends a date, hash, or version suffix to the filename. Optionally it logs every move to a report. Optionally it notifies you by email when a run completes.</p>

      <h2>Built for These Industries</h2>

      <h3>Law Firms</h3>
      <p>Sort case files, contracts, discovery PDFs, court orders, client correspondence, billing records, and signed engagement letters into matter-specific folders, automatically. Keep everything on-premise so client confidentiality is preserved. No cloud DMS subscription draining the partnership profit pool.</p>

      <h3>Accounting and Audit Practices</h3>
      <p>Sort tax returns, GST/VAT filings, expense receipts, bank statements, payroll reports, audit work papers, and engagement letters into the right client folder, automatically. End-of-year season becomes manageable. Replace one part-time data-entry hire with a USD 499 software license.</p>

      <h3>Hospitals, Clinics, and Healthcare</h3>
      <p>Sort lab reports, radiology images, discharge summaries, patient consent forms, billing claims, and insurance correspondence into patient-specific or department-specific folders. No PHI ever leaves your network — HIPAA-friendly by architecture. Defensible audit trail for every file move.</p>

      <h3>Banks, Brokerages, and BFSI</h3>
      <p>Sort KYC documents, loan applications, account opening forms, trade confirmations, regulatory filings, and customer correspondence into product-specific or account-specific folders. Air-gapped operation supported. Per-machine licensing maps cleanly to branch deployment.</p>

      <h3>Government and Public Sector</h3>
      <p>Sort RTI/FOIA requests, citizen applications, internal memos, vendor invoices, and tender documents into department-specific folders. No data leaves the agency network. Suitable for offices subject to data-sovereignty and procurement-policy restrictions.</p>

      <h3>Manufacturing and Logistics</h3>
      <p>Sort purchase orders, invoices, bills of lading, customs paperwork, quality certificates, and vendor correspondence into project-specific or supplier-specific folders. Works on factory-floor PCs with intermittent network connectivity.</p>

      <h3>Real Estate, Insurance, and Property</h3>
      <p>Sort lease agreements, property deeds, valuation reports, insurance policies, claim forms, and customer correspondence into property-specific or client-specific folders. Reduces the per-file cost dramatically when document volume grows seasonally.</p>

      <h3>DMS Vendors and IT Resellers</h3>
      <p>Bundle Techno Sort as the classification layer in your existing document management offering. Per-machine licensing means stable margins. No vendor portal sits between you and your customer. Available for white-labelling and OEM arrangements by email request.</p>

      <h2>What You Get in Each Tier</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-sm border border-white/10">
          <thead>
            <tr className="bg-white/[0.04]">
              <th className="text-left p-3 border-b border-white/10">Feature</th>
              <th className="text-center p-3 border-b border-white/10">Free Trial</th>
              <th className="text-center p-3 border-b border-white/10 text-[#F4A22A]">Pro 1-Year</th>
              <th className="text-center p-3 border-b border-white/10">Lifetime</th>
            </tr>
          </thead>
          <tbody>
            {TIER_MATRIX.map((row, i) => (
              <tr key={i} className="border-b border-white/5 last:border-0">
                <td className="p-3 text-neutral-300">{row[0]}</td>
                {[row[1], row[2], row[3]].map((v, j) => (
                  <td key={j} className="p-3 text-center">
                    {v ? <Check className="w-4 h-4 text-[#F4A22A] inline" /> : <X className="w-4 h-4 text-neutral-600 inline" />}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-b border-white/5 bg-white/[0.02]">
              <td className="p-3 text-white font-medium">Document quota</td>
              <td className="p-3 text-center text-neutral-300">300 total</td>
              <td className="p-3 text-center text-[#F4A22A]">14,999 per year</td>
              <td className="p-3 text-center text-white">Unlimited</td>
            </tr>
            <tr className="border-b border-white/5 bg-white/[0.02]">
              <td className="p-3 text-white font-medium">Refill Pack eligibility</td>
              <td className="p-3 text-center">once</td>
              <td className="p-3 text-center">unlimited</td>
              <td className="p-3 text-center">not needed</td>
            </tr>
            <tr className="border-b border-white/5 bg-white/[0.02]">
              <td className="p-3 text-white font-medium">Price</td>
              <td className="p-3 text-center">USD 0</td>
              <td className="p-3 text-center text-[#F4A22A]">USD 199 / year</td>
              <td className="p-3 text-center text-white">USD 499 once</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Common Myths, Busted</h2>
      {MYTHS.map((m, i) => (
        <div key={i}>
          <h3>Myth — {m.m}</h3>
          <p><strong>Reality.</strong> {m.r}</p>
        </div>
      ))}

      <h2>Try It Today</h2>
      <p><strong>Free Trial. 300 documents. Zero risk.</strong></p>
      <p>
        Download Techno Sort, install in 5 minutes, classify 300 of your own documents at no cost. If you need more for evaluation, the optional Refill Pack adds 1,500 documents for USD 10. That is 1,800 of your real documents sorted before you decide whether to buy. After that, choose Pro 1-Year for USD 199 or Lifetime for USD 499. No questions asked. No sales call required. No SaaS portal to provision. Just sort.
      </p>

      <h2>Three Lines That Sum Up Techno Sort</h2>
      <ul>
        <li>✓ 100% offline — your documents never leave your network.</li>
        <li>✓ One-time license — buy it once, no monthly bills, no SaaS lock-in.</li>
        <li>✓ Single EXE install — no Python, no Docker, no cloud account.</li>
      </ul>

      <p>Visit our website to download the Free Trial. All communications by email: <strong>technosort.rd@gmail.com</strong></p>
      <p><em>— Welcome to a documents pile that finally sorts itself —</em></p>
    </ProtectedLegalPage>
  );
}
